import useAccountingFirmPermissions from "hooks/Permissions/useAccountingFirmPermissions";
import useCompanyPermissions from "hooks/Permissions/useCompanyPermissions";
import useGeneralPermissions from "hooks/Permissions/useGeneralPermissions";

/**
 * Maps a route's `requiredPermission` to whether the current user has it.
 * A route that fails its check is sent to `/`, the only route no permission
 * guards — see `PermissionRedirect`.
 */
export function useRoutePermissions() {
  const {
    canUpdateProfile,
    canCreateUser,
    canDisplayUser,
    canUpdateUser,
    canViewBanks,
    canDisplayFeed,
    isDirector,
  } = useGeneralPermissions();

  const {
    canCreateAccountingFirm,
    canViewAccountingFirmSettings,
    canUpdateAccountingFirmSettings,
    canDisplayAccountingFirms,
    canDisplayAccountingFirmAddress,
    canDisplayAccountingFirmContacts,
    canDisplayServices,
    canDisplayPlatforms,
  } = useAccountingFirmPermissions();

  const {
    canDisplayBooks,
    canDisplayClients,
    canDisplayProviders,
    canCreateCompany,
    canDisplayCompanySettings,
    canDisplayAssignments,
    canDisplayCompanyAddress,
    canDisplayCompanyLegal,
    canDisplayCorporateDirectors,
    canDisplayCorporateStakeholders,
    canDisplayCompanyGeneral,
    canImport,
    canExport,
    canDisplayCounterParts,
    canDisplayVats,
    canDisplayCycles,
    canDisplaySubsidiaries,
    canDisplayCompaniesList,
  } = useCompanyPermissions();

  return {
    displayAccountingFirms: canDisplayAccountingFirms,
    createAccountingFirms: canCreateAccountingFirm,
    canDisplayAccountingBanks: canViewBanks,
    canDisplayAccountingAdress: canDisplayAccountingFirmAddress,
    canDisplayAccountingContacts: canDisplayAccountingFirmContacts,
    canDisplayAccountingPlatforms: canDisplayPlatforms,
    canDisplayAccountingServices: canDisplayServices,
    canDisplayAccountingCompanies: canDisplayCompaniesList,
    canDisplayAccountingSettings: canViewAccountingFirmSettings,
    canManageCounterpartSsiRules: isDirector && canUpdateAccountingFirmSettings,

    canDisplayFeed,

    displayUsers: canDisplayUser,
    createUsers: canCreateUser,
    updateUsers: canUpdateUser,
    updateProfile: canUpdateProfile,

    displayCompanies: canDisplayCompaniesList,
    createCompanies: canCreateCompany,
    canDisplayCompanyGeneral,
    canDisplayCompaniesBanks: canViewBanks,
    canDisplayCompanyAddress,
    canDisplayAssignments,
    canDisplayCompanyLegal,
    canDisplayCorporateDirectors,
    canDisplayCorporateStakeholders,
    canDisplayCompanySettings,
    canDisplayBooks,
    canDisplayVats,
    canDisplayClients,
    canDisplayProviders,
    canDisplayCounterParts,
    canDisplayCycles,
    canDisplaySubsidiaries,
    canImport,
    canExport,
  };
}

/**
 * Every value `requiredPermission` accepts, so a misspelled or renamed key is a
 * compile error. `"none"` is for routes every authenticated user can see — it is
 * required rather than optional so a new route cannot silently skip the check.
 */
export type RoutePermission = keyof ReturnType<typeof useRoutePermissions> | "none";
